import { Check, DotsThreeVertical, Trash } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ClipLoader } from 'react-spinners';

type Address = {
  address_id: string;
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  is_primary?: boolean;
};

export const AddressCard = ({
  address,
  activePopup,
  loadingDelete,
  loadingMakePrimary,
  deleteAddress,
  makePrimary,
  popupRefs,
  togglePopup,
}: {
  address: Address;
  activePopup: string | null;
  popupRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
  togglePopup?: (address_id: string | undefined) => void;
  loadingDelete?: boolean;
  deleteAddress: (id: string) => Promise<void>;
  makePrimary: (id: string) => Promise<void>;
  loadingMakePrimary?: boolean;
}) => {
  const streetMain = [address.address_st_number, address.address_st_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  const line1 = address.address_unit
    ? `${streetMain}, ${address.address_unit}`
    : streetMain;

  const line2 = [address.city, address.state, address.postal_code]
    .filter(Boolean)
    .join(', ');

  const [buttonPositions, setButtonPositions] = useState<{
    [key: string]: { top: number; left: number; width: number; height: number };
  }>({});

  // Update button position when activePopup changes
  useEffect(() => {
    if (activePopup) {
      const buttonElement = document.getElementById(
        `popup-button-${activePopup}`
      );
      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        setButtonPositions((prev) => ({
          ...prev,
          [activePopup]: {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          },
        }));
      }
    }
  }, [activePopup]);

  return (
    <div className="w-full rounded-md border border-bordergray bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-primary">{line1}</p>
          <p className="text-sm text-graysecondary">{line2}</p>
        </div>

        <div className="flex items-center gap-3">
          {address.is_primary ? (
            <span className="flex items-center gap-1 pr-1 text-green-600">
              <span className="text-[12px] font-semibold">Primary</span>
              <Check size={16} />
            </span>
          ) : (
            <>
              <button
                type="button"
                id={`popup-button-${address.address_id}`}
                onClick={(e: any) => {
                  e.stopPropagation();
                  togglePopup?.(address.address_id);
                }}
                className="pointer relative rounded hover:bg-[#F2F4F7]"
                aria-label="More options"
              >
                <DotsThreeVertical size={22} weight="bold" />
                {activePopup === address.address_id &&
                  createPortal(
                    <div
                      ref={(el) => {
                        popupRefs.current[address.address_id ?? ''] = el;
                      }}
                      className="fixed z-40 w-[200px] rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                      style={{
                        position: 'absolute',
                        top: `calc(${buttonPositions[address.address_id]?.top || 0}px + 30px)`,
                        left: `calc(${buttonPositions[address.address_id]?.left || 0}px - 15px)`,
                        transform: 'translate(-80%, 0)',
                      }}
                    >
                      <div className="flex flex-col">
                        <button
                          onClick={async (e: any) => {
                            e.stopPropagation();
                            await makePrimary(address.address_id);
                            togglePopup?.(address.address_id);
                          }}
                          className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                        >
                          <Check
                            size={20}
                            className="h-[20px] min-h-[20px] w-[20px] min-w-[20px]"
                          />
                          <p>Make Primary</p>
                          {loadingMakePrimary && (
                            <ClipLoader
                              color="#1D213B"
                              loading={loadingMakePrimary}
                              size={20}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                              className="h-full w-full justify-center"
                            />
                          )}
                        </button>
                        <button
                          onClick={async (e: any) => {
                            e.stopPropagation();
                            await deleteAddress(address.address_id);
                            togglePopup?.(address.address_id);
                          }}
                          className="flex w-full flex-row items-center space-x-1 rounded-lg px-4 py-2 text-left align-middle text-danger hover:bg-gray-100"
                        >
                          <Trash
                            size={20}
                            className="h-[20px] min-h-[20px] w-[20px] min-w-[20px]"
                          />
                          <p>Delete</p>
                          {loadingDelete && (
                            <ClipLoader
                              color="#1D213B"
                              loading={loadingDelete}
                              size={20}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                              className="h-full w-full justify-center"
                            />
                          )}
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
